import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { DollarSign, Receipt, Calculator, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart as RechartsPie, Pie, Cell } from 'recharts';

export const SummaryTab = ({ loading, summaryData, year }) => {
  if (loading) return <div className="text-center py-12">Loading financial summary...</div>;
  if (!summaryData) return null;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="card-revenue">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-semibold font-mono text-[hsl(188,64%,38%)]">
                  {formatCurrency(summaryData.summary.total_revenue)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summaryData.summary.invoice_count} invoices
                </p>
              </div>
              <div className="p-3 rounded-full bg-[hsl(188,64%,95%)]">
                <DollarSign className="w-6 h-6 text-[hsl(188,64%,38%)]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-expenses">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-semibold font-mono text-[hsl(4,79%,55%)]">
                  {formatCurrency(summaryData.summary.total_expenses)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summaryData.summary.expense_count} expenses
                </p>
              </div>
              <div className="p-3 rounded-full bg-red-50">
                <Receipt className="w-6 h-6 text-[hsl(4,79%,55%)]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-profit">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-semibold font-mono ${summaryData.summary.net_profit >= 0 ? 'text-[hsl(164,62%,40%)]' : 'text-[hsl(4,79%,55%)]'}`}>
                  {formatCurrency(summaryData.summary.net_profit)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summaryData.summary.profit_margin.toFixed(1)}% margin
                </p>
              </div>
              <div className={`p-3 rounded-full ${summaryData.summary.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {summaryData.summary.net_profit >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-[hsl(164,62%,40%)]" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-[hsl(4,79%,55%)]" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-vat">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net VAT</p>
                <p className="text-2xl font-semibold font-mono">
                  {formatCurrency(summaryData.vat_summary.net_vat)}
                </p>
                <Badge variant={summaryData.vat_summary.status === 'Payable' ? 'secondary' : 'outline'} className="mt-1">
                  {summaryData.vat_summary.status}
                </Badge>
              </div>
              <div className="p-3 rounded-full bg-amber-50">
                <Calculator className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue vs Expenses Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Revenue vs Expenses - {year}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={summaryData.monthly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="hsl(4, 79%, 55%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Quarterly VAT Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quarterly VAT Summary</CardTitle>
            <CardDescription>Output VAT, Input VAT, and Net VAT by quarter</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quarter</TableHead>
                  <TableHead className="text-right">Output VAT</TableHead>
                  <TableHead className="text-right">Input VAT</TableHead>
                  <TableHead className="text-right">Net VAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaryData.quarterly_vat.map((q) => (
                  <TableRow key={q.quarter}>
                    <TableCell className="font-medium">{q.quarter} {year}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(q.output_vat)}</TableCell>
                    <TableCell className="text-right font-mono text-red-500">{formatCurrency(q.input_vat)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(q.net_vat)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50">
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {formatCurrency(summaryData.vat_summary.output_vat)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-red-500">
                    {formatCurrency(summaryData.vat_summary.input_vat)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-lg">
                    {formatCurrency(summaryData.vat_summary.net_vat)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Collection Performance</CardTitle>
            <CardDescription>Revenue collected vs outstanding</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 rounded-lg bg-[hsl(188,64%,95%)]">
                <div>
                  <p className="text-sm text-muted-foreground">Collected</p>
                  <p className="text-2xl font-semibold font-mono text-[hsl(188,64%,38%)]">
                    {formatCurrency(summaryData.summary.total_collected)}
                  </p>
                </div>
                <ArrowUpRight className="w-8 h-8 text-[hsl(188,64%,38%)]" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50">
                <div>
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-semibold font-mono text-amber-600">
                    {formatCurrency(summaryData.summary.total_outstanding)}
                  </p>
                </div>
                <ArrowDownRight className="w-8 h-8 text-amber-600" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                <div>
                  <p className="text-sm text-muted-foreground">Collection Rate</p>
                  <p className="text-2xl font-semibold">
                    {summaryData.summary.collection_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="w-16 h-16">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: 'Collected', value: summaryData.summary.collection_rate },
                          { name: 'Outstanding', value: 100 - summaryData.summary.collection_rate }
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={15}
                        outerRadius={30}
                      >
                        <Cell fill="hsl(188, 64%, 38%)" />
                        <Cell fill="hsl(38, 95%, 55%)" />
                      </Pie>
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
